import cx from 'classnames'
import dayjs from 'dayjs'
import { first } from 'lodash'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { isIOS } from 'lib/device'
import { EXTERNAL_PATH, ROUTE_PATH } from 'lib/paths'
import { unWei } from 'lib/helpers'
import { EmplTransaction } from 'lib/_types'
import { Gift } from 'lib/_types/gift'
import { useModal } from 'store/slices/modals'
import { useGetWalletEmplTransactionDetailsMutation } from 'store/api/yay/misc'
import {
  useGetGiftExchangeTransactionDetailsQuery,
  useGetReceivedGiftTransactionDetailsQuery,
} from 'store/api/yay/gifts'

import { useEMPLDetails } from 'features/Bag/useBagDetails'

import ImageLoader from 'components/ImageLoader'
import Avatar from 'components/Avatar'
import { ExternalLink } from 'components/ExternalLink'

import './style.scss'

export const useTransactionDetailsModal = () =>
  useModal<EmplTransaction>('TransactionDetailsModal')

const GiftTransactionDetails = ({
  transaction,
}: {
  transaction: Partial<EmplTransaction>
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const transactionDetailsModal = useTransactionDetailsModal()
  const onClose = transactionDetailsModal.onClose
  const getTransactionDetailsRes = useGetReceivedGiftTransactionDetailsQuery({
    uuid: transaction.gift_transaction_uuid,
  })

  const [giftItem, setGiftItem] = useState(null)

  useEffect(() => {
    if (getTransactionDetailsRes.isSuccess) {
      setGiftItem(first(getTransactionDetailsRes?.data?.gifts))
    }
  }, [getTransactionDetailsRes?.data])

  return (
    <Fragment>
      <div className="TransactionDetails__summary">
        <div className="TransactionDetails__summary__heading">
          {t('empl_transaction.send_gift')}
        </div>
        <div className="TransactionDetails__summary__image TransactionDetails__summary__image--gift">
          <ImageLoader url={giftItem?.item?.icon?.url} />
        </div>
        <div className="TransactionDetails__summary__subheading">
          {giftItem?.item?.title}
        </div>
      </div>
      <div className="TransactionDetails__dl">
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.price')}
          </div>
          <div className="TransactionDetails__dd">
            <div className="TransactionDetails__dd__values">
              {['rewarded', 'regular'].map((emplType) => {
                const amount = unWei(Number(transaction.empl?.[emplType]))
                if (amount !== 0) {
                  return (
                    <div
                      key={emplType}
                      className={`TransactionDetails__value TransactionDetails__value--${emplType}`}
                    >
                      {amount?.toLocaleString()} EMPL
                    </div>
                  )
                }
              })}
            </div>
          </div>
        </div>
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.recipient')}
          </div>
          <div className="TransactionDetails__dd">
            {getTransactionDetailsRes?.data?.receiver && (
              <div className="UserInfo__wrapper">
                <a
                  className="UserInfo__handle"
                  onClick={() => {
                    onClose()
                    navigate(
                      ROUTE_PATH.USER(
                        getTransactionDetailsRes?.data?.receiver?.id
                      )
                    )
                  }}
                />
                <div className="UserInfo__icon">
                  <Avatar user={getTransactionDetailsRes?.data?.receiver} />
                </div>
                <div className="UserInfo__name">
                  {getTransactionDetailsRes?.data?.receiver?.nickname}
                </div>
              </div>
            )}
            {!getTransactionDetailsRes?.data?.receiver && (
              <>{t('empl_transaction.user_does_not_exist')}</>
            )}
          </div>
        </div>
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.date')}
          </div>
          <div className="TransactionDetails__dd">
            <time
              className="PostTime"
              title={dayjs(
                getTransactionDetailsRes?.data?.created_at * 1000
              ).format()}
            >
              {dayjs(getTransactionDetailsRes?.data?.created_at * 1000).format(
                'YYYY/MM/DD HH:mm:ss'
              )}
            </time>
          </div>
        </div>
      </div>
    </Fragment>
  )
}

const WalletTransactionDetails = ({
  transaction,
}: {
  transaction: Partial<EmplTransaction>
}) => {
  const { t } = useTranslation()
  const emplDetailsRes = useEMPLDetails()
  const [getTransactionDetails, getTransactionDetailsRes] =
    useGetWalletEmplTransactionDetailsMutation()

  // NOTE: Currenly unused because this does not return any data not already available, but keeping in case it's useful for the future...??

  // const [getWalletTransactionDetails, getWalletTransactionDetailsRes] =
  //  useGetWalletTransactionDetailsMutation()

  const heading = useMemo(() => {
    switch (transaction.type) {
      case 'bag.deposit':
        return t('empl_transaction.deposit')
      case 'bag.withdrawal':
        return t('empl.exchange')
    }
  }, [transaction])

  const { displayAmount, displayStatus } = useMemo(() => {
    let displayAmount = '--'
    let displayStatus = '--'

    if (getTransactionDetailsRes.isLoading) {
      displayAmount = t('common.loading')
      displayStatus = t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      const amount = unWei(
        Number(getTransactionDetailsRes?.data?.result?.amount)
      )
      displayAmount = `${amount?.toLocaleString()} EMPL`
      displayStatus = t('empl_transaction.status.success')
    }

    return { displayAmount, displayStatus }
  }, [getTransactionDetailsRes])

  useEffect(() => {
    if (transaction.id) {
      getTransactionDetails({
        id: transaction.id?.toString(),
      })
    }
  }, [transaction])

  return (
    <Fragment>
      <div className="TransactionDetails__summary">
        <div className="TransactionDetails__summary__heading">{heading}</div>
        <div className="TransactionDetails__summary__image TransactionDetails__summary__image--empl">
          <ImageLoader url="/images/icon-empl-coin.svg" />
        </div>
        <div className="TransactionDetails__summary__emplAmount">
          {displayAmount}
        </div>
      </div>
      <div className="TransactionDetails__dl">
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.status')}
          </div>
          <div className="TransactionDetails__dd">
            <span
              className={cx('TransactionDetails__dd', {
                'TransactionDetails__dd--success':
                  getTransactionDetailsRes?.data?.result?.status === 'success',
              })}
            >
              {displayStatus}
            </span>
            <br />
            <time
              className="PostTime"
              title={dayjs(transaction.occurred_at * 1000).format()}
            >
              {dayjs(transaction.occurred_at * 1000).format(
                'YYYY/MM/DD HH:mm:ss'
              )}
            </time>
          </div>
        </div>
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">Deposit from</div>
          <div className="TransactionDetails__dd">Your wallet</div>
        </div>
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">Network</div>
          <div className="TransactionDetails__dd">Soneium Mainnet</div>
        </div>
      </div>
    </Fragment>
  )
}

const EmplTransactionDetails = ({
  transaction,
}: {
  transaction: Partial<EmplTransaction>
}) => {
  const { t } = useTranslation()
  const emplDetailsRes = useEMPLDetails()
  const [getTransactionDetails, getTransactionDetailsRes] =
    useGetWalletEmplTransactionDetailsMutation()

  const heading = useMemo(() => {
    switch (transaction.type) {
      case 'purchase.yay.empl':
      case 'admin.add':
      case 'dev.add':
      case 'iap.purchase':
      case 'square.purchase':
      case 'slash.purchase':
        return t('empl_transaction.purchase_empl')
      case 'coin.convert':
      case 'bag.withdrawal_tokens':
        return t('empl.exchange')
      case 'bag.expired':
        return t('empl.expired')
      case 'admin.deduct':
        return 'Deduction'
      case 'bag.withdrawal_refund':
        return t('empl_transaction.refund')
    }
  }, [transaction])

  const { displayAmount, displayStatus, displayPrice } = useMemo(() => {
    let displayAmount = '--'
    let displayStatus = '--'
    let displayPrice = '--'

    if (getTransactionDetailsRes.isLoading) {
      displayAmount = t('common.loading')
      displayStatus = t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      const amount = unWei(
        Number(getTransactionDetailsRes?.data?.result?.amount)
      )
      displayAmount = `${amount?.toLocaleString()} EMPL`
      displayStatus = t('empl_transaction.status.success')
      displayPrice = getTransactionDetailsRes?.data?.result?.price
        ? `¥${getTransactionDetailsRes?.data?.result?.price?.toLocaleString()}`
        : null
    }

    return { displayAmount, displayStatus, displayPrice }
  }, [getTransactionDetailsRes])

  useEffect(() => {
    if (transaction.id) {
      getTransactionDetails({
        id: transaction.id?.toString(),
      })
    }
  }, [transaction])

  return (
    <Fragment>
      <div className="TransactionDetails__summary">
        <div className="TransactionDetails__summary__heading">{heading}</div>
        <div className="TransactionDetails__summary__image TransactionDetails__summary__image--empl">
          <ImageLoader url="/images/icon-empl-coin.svg" />
        </div>
        <div className="TransactionDetails__summary__emplAmount">
          {displayAmount}
        </div>
      </div>
      <div className="TransactionDetails__dl">
        {displayPrice &&
          !['admin.add', 'admin.deduct', 'bag.expired', 'dev.add'].includes(
            transaction.type
          ) && (
            <div className="TransactionDetails__detailRow">
              <div className="TransactionDetails__dt">
                {t('empl.transaction_details.price')}
              </div>
              <div className="TransactionDetails__dd">
                <div className="TransactionDetails__dd__values">
                  {displayPrice}
                </div>
              </div>
            </div>
          )}
        {['bag.expired', 'bag.withdrawal_refund'].includes(
          transaction.type
        ) && (
          <div className="TransactionDetails__detailRow">
            <div className="TransactionDetails__dt">
              {t('empl.transaction_details.amount')}
            </div>
            <div className="TransactionDetails__dd">
              <div className="TransactionDetails__dd__values">
                {['rewarded', 'regular'].map((emplType) => {
                  const amount = unWei(Number(transaction.empl?.[emplType]))
                  const isPositive = Number(transaction.amount) > 0

                  if (amount !== 0) {
                    return (
                      <div
                        key={emplType}
                        className={`TransactionDetails__value TransactionDetails__value--${emplType}`}
                      >
                        <span
                          className={cx({
                            'EMPLTransactionItem__value--green': isPositive,
                          })}
                        >
                          {isPositive ? '+' : '-'}
                          {amount?.toLocaleString()}
                        </span>{' '}
                        EMPL
                      </div>
                    )
                  }
                })}
              </div>
            </div>
          </div>
        )}
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.status')}
          </div>
          <div className="TransactionDetails__dd">
            <span
              className={cx('TransactionDetails__dd', {
                'TransactionDetails__dd--success':
                  getTransactionDetailsRes?.data?.result?.status === 'success',
              })}
            >
              {displayStatus}
            </span>
            <br />
            <time
              className="PostTime"
              title={dayjs(transaction.occurred_at * 1000).format()}
            >
              {dayjs(transaction.occurred_at * 1000).format(
                'YYYY/MM/DD HH:mm:ss'
              )}
            </time>
          </div>
        </div>
      </div>
    </Fragment>
  )
}

const ExchangeTransactionDetails = ({
  transaction,
}: {
  transaction: Partial<EmplTransaction>
}) => {
  const { t } = useTranslation()
  const getTransactionDetailsRes = useGetGiftExchangeTransactionDetailsQuery({
    uuid: transaction.gift_exchange_uuid,
  })

  const displayAmount = useMemo(() => {
    if (getTransactionDetailsRes.isLoading) {
      return t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      return `${Number(
        getTransactionDetailsRes?.data?.rewarded_amount
      ).toLocaleString()} EMPL`
    } else {
      return '--'
    }
  }, [getTransactionDetailsRes])

  const displayStatus = useMemo(() => {
    if (getTransactionDetailsRes.isLoading) {
      return t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      return t('empl_transaction.status.success')
    } else {
      return '--'
    }
  }, [getTransactionDetailsRes])

  const displayTotalQty = useMemo(() => {
    if (getTransactionDetailsRes.isLoading) {
      return t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      return getTransactionDetailsRes?.data?.items
        ?.map((g) => g.quantity)
        .reduce((prev, current, i, list) => prev + current, 0)
    } else {
      return '--'
    }
  }, [getTransactionDetailsRes])

  return (
    <Fragment>
      <div className="TransactionDetails__summary">
        <div className="TransactionDetails__summary__heading">
          {t('empl.exchange')}
        </div>
        <div className="TransactionDetails__summary__image TransactionDetails__summary__image--empl">
          <ImageLoader url="/images/icon-empl-coin.svg" />
        </div>
        <div className="TransactionDetails__summary__emplAmount">
          {displayAmount}
        </div>
      </div>
      <div className="TransactionDetails__dl">
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.status')}
          </div>
          <div className="TransactionDetails__dd">
            <span
              className={cx('TransactionDetails__dd', {
                'TransactionDetails__dd--success':
                  getTransactionDetailsRes?.data?.status === 'success',
              })}
            >
              {displayStatus}
            </span>
            <br />
            <time
              className="PostTime"
              title={dayjs(transaction.occurred_at * 1000).format()}
            >
              {dayjs(transaction.occurred_at * 1000).format(
                'YYYY/MM/DD HH:mm:ss'
              )}
            </time>
          </div>
        </div>
      </div>
      <div className="TransactionDetails__dl TransactionDetails__h3">
        {t('empl.transaction_details.exchange_summary_description', {
          qty: displayTotalQty,
        })}
      </div>
      <div className="TransactionDetails__dl">
        {getTransactionDetailsRes.data?.items?.map(
          (item: { quantity: number; gift: Gift }, i: number) => {
            return (
              <div className="TransactionDetails__detailRow" key={`gift_${i}`}>
                <div className="TransactionDetails__dt">
                  <div className="GiftLabel">
                    <ImageLoader
                      className="GiftLabel__icon"
                      url={item.gift?.icon_thumbnail}
                    />
                    <div className="GiftLabel__title">{item.gift?.title}</div>
                  </div>
                </div>
                <div className="TransactionDetails__dd--black">
                  {item.quantity}
                </div>
              </div>
            )
          }
        )}
      </div>
    </Fragment>
  )
}

const WithdrawalTransactionDetails = ({
  transaction,
}: {
  transaction: Partial<EmplTransaction>
}) => {
  const { t } = useTranslation()
  const [getTransactionDetails, getTransactionDetailsRes] =
    useGetWalletEmplTransactionDetailsMutation()

  const heading = useMemo(() => {
    switch (transaction.type) {
      case 'bag.withdrawal_to_yay':
        return t('empl_transaction.withdrawal')
      case 'bag.withdrawal_tokens':
        return t('empl.exchange')
    }
  }, [transaction])

  const displayAmount = useMemo(() => {
    if (getTransactionDetailsRes.isLoading) {
      return t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      return `${unWei(
        Number(getTransactionDetailsRes?.data?.result?.amount) * -1
      ).toLocaleString()} EMPL`
    } else {
      return '--'
    }
  }, [getTransactionDetailsRes])

  const displayFeeAmount = useMemo(() => {
    if (getTransactionDetailsRes.isLoading) {
      return t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      return `${unWei(
        (Number(getTransactionDetailsRes?.data?.result?.transaction_fee) * -1) |
          0
      ).toLocaleString()} EMPL`
    } else {
      return '--'
    }
  }, [getTransactionDetailsRes])

  const displayStatus = useMemo(() => {
    if (getTransactionDetailsRes.isLoading) {
      return t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      return t('empl_transaction.status.success')
    } else {
      return '--'
    }
  }, [getTransactionDetailsRes])

  const displayWalletAddress = useMemo(() => {
    if (getTransactionDetailsRes.isLoading) {
      return t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      return getTransactionDetailsRes?.data?.result?.wallet_address
    } else {
      return '--'
    }
  }, [getTransactionDetailsRes])

  useEffect(() => {
    if (transaction.id) {
      getTransactionDetails({
        id: transaction.id?.toString(),
      })
    }
  }, [transaction])

  return (
    <Fragment>
      <div className="TransactionDetails__summary">
        <div className="TransactionDetails__summary__heading">{heading}</div>
        <div className="TransactionDetails__summary__image TransactionDetails__summary__image--empl">
          <ImageLoader
            url={`/images/icon-${
              transaction.token_symbol
                ? `token-${transaction.token_symbol}.png`
                : 'wallet.svg'
            }`}
          />
        </div>
        <div className="TransactionDetails__summary__emplAmount">
          {displayAmount}
        </div>
      </div>
      <div className="TransactionDetails__dl">
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.amount')}
          </div>
          <div className="TransactionDetails__dd">
            <div className="TransactionDetails__dd__values">
              {['rewarded', 'regular'].map((emplType) => {
                const amount = unWei(Number(transaction.empl?.[emplType]))
                if (amount !== 0) {
                  return (
                    <div
                      key={emplType}
                      className={`TransactionDetails__value TransactionDetails__value--${emplType}`}
                    >
                      {amount?.toLocaleString()} EMPL
                    </div>
                  )
                }
              })}
            </div>
          </div>
        </div>
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.status')}
          </div>
          <div className="TransactionDetails__dd">
            <span
              className={cx('TransactionDetails__dd', {
                'TransactionDetails__dd--success':
                  getTransactionDetailsRes?.data?.result?.status === 'success',
              })}
            >
              {displayStatus}
            </span>
            <br />
            <time
              className="PostTime"
              title={dayjs(transaction.occurred_at * 1000).format()}
            >
              {dayjs(transaction.occurred_at * 1000).format(
                'YYYY/MM/DD HH:mm:ss'
              )}
            </time>
          </div>
        </div>
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.wallet_address')}
          </div>
          <div className="TransactionDetails__dd">{displayWalletAddress}</div>
        </div>
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.transaction_fee')}
          </div>
          <div className="TransactionDetails__dd">{displayFeeAmount}</div>
        </div>
      </div>
    </Fragment>
  )
}

const PalTransactionDetails = ({
  transaction,
}: {
  transaction: Partial<EmplTransaction>
}) => {
  const { t } = useTranslation()
  const [getTransactionDetails, getTransactionDetailsRes] =
    useGetWalletEmplTransactionDetailsMutation()
  // const [getWalletTransactionDetails, getWalletTransactionDetailsRes] =
  // 	useGetWalletTransactionDetailsMutation()

  const heading = useMemo(() => {
    switch (transaction.type) {
      case 'pal.level_up':
        return t('empl_transaction.pal_level_up')
      case 'pal.withdrawal_fee':
        return t('empl_transaction.pal_withdrawal_fee_description')
    }
  }, [transaction])

  const { displayAmount, displayStatus, displayPrice } = useMemo(() => {
    let displayAmount = '--'
    let displayStatus = '--'
    let displayPrice = '--'

    if (getTransactionDetailsRes.isLoading) {
      displayAmount = t('common.loading')
      displayStatus = t('common.loading')
    } else if (
      getTransactionDetailsRes.isSuccess &&
      getTransactionDetailsRes.data
    ) {
      const amount = unWei(
        Number(getTransactionDetailsRes?.data?.result?.amount)
      )
      displayAmount = `${amount?.toLocaleString()} EMPL`
      displayStatus = t('empl_transaction.status.success')
      displayPrice = getTransactionDetailsRes?.data?.result?.price
        ? `¥${getTransactionDetailsRes?.data?.result?.price?.toLocaleString()}`
        : null
    }

    return { displayAmount, displayStatus, displayPrice }
  }, [getTransactionDetailsRes])

  useEffect(() => {
    if (transaction.id) {
      getTransactionDetails({
        id: transaction.id?.toString(),
      })
    }
  }, [transaction])

  return (
    <Fragment>
      <div className="TransactionDetails__summary">
        <div className="TransactionDetails__summary__heading">{heading}</div>
        <div className="TransactionDetails__summary__image TransactionDetails__summary__image--empl">
          <ImageLoader url="/images/icon-pal.svg" />
        </div>
      </div>
      {transaction.type == 'pal.withdrawal_fee' && (
        <div className="TransactionDetails__dl TransactionDetails__h3">
          {t('empl_transaction.pal_withdrawal_fee_details_description')}
        </div>
      )}
      <div className="TransactionDetails__dl">
        {displayPrice && (
          <div className="TransactionDetails__detailRow">
            <div className="TransactionDetails__dt">
              {t('empl.transaction_details.price')}
            </div>
            <div className="TransactionDetails__dd">
              <div className="TransactionDetails__dd__values">
                {displayPrice}
              </div>
            </div>
          </div>
        )}
        {displayAmount && (
          <div className="TransactionDetails__detailRow">
            <div className="TransactionDetails__dt">
              {t('empl.transaction_details.amount')}
            </div>
            <div className="TransactionDetails__dd">
              <div className="TransactionDetails__dd__values">
                {['rewarded', 'regular'].map((emplType) => {
                  const amount = unWei(Number(transaction.empl?.[emplType]))
                  if (amount !== 0) {
                    return (
                      <div
                        key={emplType}
                        className={`TransactionDetails__value TransactionDetails__value--${emplType}`}
                      >
                        -{amount?.toLocaleString()} EMPL
                      </div>
                    )
                  }
                })}
              </div>
            </div>
          </div>
        )}
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.status')}
          </div>
          <div className="TransactionDetails__dd">
            <span
              className={cx('TransactionDetails__dd', {
                'TransactionDetails__dd--success':
                  getTransactionDetailsRes?.data?.result?.status === 'success',
              })}
            >
              {displayStatus}
            </span>
            <br />
            <time
              className="PostTime"
              title={dayjs(transaction.occurred_at * 1000).format()}
            >
              {dayjs(transaction.occurred_at * 1000).format(
                'YYYY/MM/DD HH:mm:ss'
              )}
            </time>
          </div>
        </div>
      </div>
      <div className="TransactionDetails__footer">
        <ExternalLink
          href={
            isIOS
              ? EXTERNAL_PATH.DOWNLOAD_LINK_IOS
              : EXTERNAL_PATH.DOWNLOAD_LINK_ANDROID
          }
        >
          {t('empl_transaction.pal_see_app_more_details')}
        </ExternalLink>
      </div>
    </Fragment>
  )
}

const DotMoneyTransaction = ({
  transaction,
}: {
  transaction: Partial<EmplTransaction>
}) => {
  const { t } = useTranslation()
  const [getTransactionDetails, getTransactionDetailsRes] =
    useGetWalletEmplTransactionDetailsMutation()

  const { displayImageUrl, subheading } = useMemo(() => {
    let displayImageUrl
    let subheading = '--'

    if (getTransactionDetailsRes.isSuccess) {
      console.log(
        're-memoizing',
        getTransactionDetailsRes?.data?.result?.status
      )
      switch (getTransactionDetailsRes?.data?.result?.status) {
        case 'fail':
          displayImageUrl = 'circle-cancel.svg'
          subheading = t('empl.transaction_details.exchange_failed')
          break
        case 'pending':
          displayImageUrl = 'paper-plane.png'
          subheading = t('empl.transaction_details.exchange_pending')
          break
        case 'success':
          displayImageUrl = 'success_check.png'
          subheading = t('empl.transaction_details.exchange_success')
          break
      }
    }
    return { displayImageUrl, subheading }
  }, [getTransactionDetailsRes])

  const { displayDotMoneyAmount, displayAmount, displayStatus } =
    useMemo(() => {
      let displayDotMoneyAmount = '--'
      let displayAmount = '--'
      let displayStatus = '--'

      if (getTransactionDetailsRes.isLoading) {
        displayAmount = t('common.loading')
        displayStatus = t('common.loading')
        displayDotMoneyAmount = t('common.loading')
      } else if (
        getTransactionDetailsRes.isSuccess &&
        getTransactionDetailsRes.data
      ) {
        const amount = unWei(
          Number(getTransactionDetailsRes?.data?.result?.amount)
        )
        displayAmount = `${amount?.toLocaleString()} EMPL`
        displayStatus = t('empl_transaction.status.success')
        displayDotMoneyAmount =
          getTransactionDetailsRes?.data?.result?.money_amount?.toLocaleString()
      }

      return { displayAmount, displayStatus, displayDotMoneyAmount }
    }, [getTransactionDetailsRes])

  useEffect(() => {
    if (transaction.id) {
      getTransactionDetails({
        id: transaction.id?.toString(),
      })
    }
  }, [transaction])

  return (
    <Fragment>
      <div className="TransactionDetails__summary">
        <div className="TransactionDetails__summary__heading">
          {t('empl.dotmoney')}
        </div>
        <div className="TransactionDetails__summary__image">
          {!!displayImageUrl && <img src={`/images/${displayImageUrl}`} />}
        </div>
        <div className="TransactionDetails__summary__subheading">
          {subheading}
        </div>
      </div>

      <div className="TransactionDetails__dl">
        {displayDotMoneyAmount && (
          <div className="TransactionDetails__detailRow">
            <div className="TransactionDetails__detailRow--border">
              {displayDotMoneyAmount}{' '}
              {Number(displayDotMoneyAmount) == 1
                ? t('empl.money')
                : t('empl.money_plural')}
            </div>
          </div>
        )}
        {displayAmount && (
          <div className="TransactionDetails__detailRow">
            <div className="TransactionDetails__dt">
              {t('empl.transaction_details.amount')}
            </div>
            <div className="TransactionDetails__dd">
              <div className="TransactionDetails__dd__values">
                {['rewarded', 'regular'].map((emplType) => {
                  const amount = unWei(Number(transaction.empl?.[emplType]))
                  if (amount !== 0) {
                    return (
                      <div
                        key={emplType}
                        className={`TransactionDetails__value TransactionDetails__value--${emplType}`}
                      >
                        -{amount?.toLocaleString()} EMPL
                      </div>
                    )
                  }
                })}
              </div>
            </div>
          </div>
        )}
        <div className="TransactionDetails__detailRow">
          <div className="TransactionDetails__dt">
            {t('empl.transaction_details.date')}
          </div>
          <div className="TransactionDetails__dd">
            <time
              className="PostTime"
              title={dayjs(transaction.occurred_at * 1000).format()}
            >
              {dayjs(transaction.occurred_at * 1000).format(
                'YYYY/MM/DD HH:mm:ss'
              )}
            </time>
          </div>
        </div>
      </div>
      <div className="TransactionDetails__footer">
        <ExternalLink
          href={
            isIOS
              ? EXTERNAL_PATH.DOWNLOAD_LINK_IOS
              : EXTERNAL_PATH.DOWNLOAD_LINK_ANDROID
          }
        >
          {t('empl_transaction.pal_see_app_more_details')}
        </ExternalLink>
      </div>
    </Fragment>
  )
}

const TransactionDetailsModal = () => {
  const { t } = useTranslation()
  const transactionDetailsModal = useTransactionDetailsModal()

  const onClose = transactionDetailsModal.onClose

  const BodyElm = useMemo(() => {
    switch (transactionDetailsModal.data?.type) {
      case 'purchase.yay.gift':
        return GiftTransactionDetails
      case 'admin.add':
      case 'dev.add':
      case 'admin.deduct':
      case 'purchase.yay.empl':
      case 'coin.convert':
      case 'iap.purchase':
      case 'square.purchase':
      case 'slash.purchase':
      case 'bag.expired':
      case 'bag.withdrawal_refund':
        return EmplTransactionDetails
      case 'gift.exchange':
        return ExchangeTransactionDetails
      case 'bag.withdrawal_to_yay':
      case 'bag.withdrawal_tokens':
        return WithdrawalTransactionDetails
      case 'pal.withdrawal_fee':
      case 'pal.level_up':
      case 'purchase.pal_gacha':
        return PalTransactionDetails
      case 'bag.withdrawal':
      case 'bag.deposit':
        return WalletTransactionDetails
      case 'exchange.dotmoney':
        return DotMoneyTransaction
    }
  }, [transactionDetailsModal.data])

  return (
    <div
      className={cx('Modal TransactionDetailsModal', {
        'Modal--active': transactionDetailsModal.isActive,
      })}
    >
      <a className="Modal__overlay" onClick={() => onClose()} />
      {transactionDetailsModal.isActive && (
        <div className="Modal__wrapper Modal__wrapper--auto-h">
          <div className="Modal__header Modal__header--bdrz-t">
            <a className="Modal__header__close" onClick={() => onClose()} />

            <h2 className="Modal__header__h2">
              {t('empl.transaction_details')}
            </h2>
          </div>
          <div className="Modal__body">
            <div className="TransactionDetails__wrapper">
              <BodyElm transaction={transactionDetailsModal.data} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TransactionDetailsModal
